import { zodResolver } from "@hookform/resolvers/zod";
import {
  fromDate,
  getLocalTimeZone,
  now,
  toCalendarDateTime,
} from "@internationalized/date";
import { TimedDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/TimedDuration";
import {
  useAddVehicle,
  useGetIncidentNotes,
  useGetTrainStations,
  useUpdateVehicle,
} from "features/VehicleSidebar/VehicleList/AddVehicles/useInfoDataForVehicles";
import { useMemo, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { ActionModal } from "shared/components/ActionModal";
import {
  Combobox,
  DropsStaticAlert,
  Field,
  Input,
  Item,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "shared/components/Spor";
import { Select } from "shared/components/forms/Select";
import { useFormVerbose } from "shared/form/useFormVerbose";
import {
  AddReserveAlternativeTransportRequest,
  AddReserveAlternativeTransportRequestSchema,
  ReserveAlternativeTransport,
  vehicleTypeSchema,
} from "shared/types/alternativeTransport";
import styled from "styled-components";

const RadioGroupStyled = styled(RadioGroup)`
  display: flex;
  justify-content: flex-start;
  gap: 24px;
`;

type AddVehicleProps = {
  closeModal: () => void;
  isReserveBusTabSelected: boolean;
  editVehicle?: ReserveAlternativeTransport;
};

const AddVehicle = ({
  closeModal,
  isReserveBusTabSelected,
  editVehicle,
}: AddVehicleProps) => {
  const isEdit = !!editVehicle;
  const [searchQuery, setSearchQuery] = useState(
    editVehicle?.baseStopName ?? "",
  );

  const currentTime = now(getLocalTimeZone());
  const startTime = toCalendarDateTime(currentTime);
  const endTime = toCalendarDateTime(currentTime).add({ minutes: 30 });

  const toFormTime = (value: Date | null | undefined) =>
    value ? toCalendarDateTime(fromDate(value, getLocalTimeZone())) : undefined;

  const formMethods = useFormVerbose<AddReserveAlternativeTransportRequest>({
    resolver: zodResolver(AddReserveAlternativeTransportRequestSchema),
    defaultValues: {
      serviceType:
        editVehicle?.serviceType ??
        (isReserveBusTabSelected ? "RESERVE" : "BACKUP"),
      operatorName: editVehicle?.operatorName ?? "",
      shiftStartTime: toFormTime(editVehicle?.shiftStartTime) ?? startTime,
      shiftEndTime: toFormTime(editVehicle?.shiftEndTime) ?? endTime,
      iSirkId: editVehicle?.iSirkId ?? null,
      referenceNumber: editVehicle?.referenceNumber ?? null,
      driverPhoneNumber: editVehicle?.driverPhoneNumber ?? "",
      vehicleType:
        editVehicle?.type === "BUS" || editVehicle?.type === "MINIBUS"
          ? editVehicle.type
          : undefined,
      baseStopReference: editVehicle?.baseStopReference ?? undefined,
      stopName: editVehicle?.stopName ?? "",
    },
  });

  const {
    formState: { errors },
    reset: resetForm,
    register,
    handleSubmit,
    watch,
    setValue,
  } = formMethods;

  const serviceType = watch("serviceType");
  const isReserve = serviceType === "RESERVE";
  const [isIsirkSelected, setIsIsirkSelected] = useState(
    isEdit ? !!editVehicle?.iSirkId : true,
  );
  const [date] = useState(new Date());

  const {
    data: operationalPointResponse,
    isSuccess: isOpPointResponseSuccess,
    isError: isOpPointResponseError,
  } = useGetTrainStations();

  const {
    mutate: postAddVehicle,
    status: postAddVehicleStatus,
    reset: resetStatus,
  } = useAddVehicle();

  const {
    mutate: putUpdateVehicle,
    status: updateVehicleStatus,
    reset: resetUpdateStatus,
  } = useUpdateVehicle(editVehicle?.id ?? "");

  const mutationStatus = isEdit ? updateVehicleStatus : postAddVehicleStatus;

  const { data: notes, isError: isNotesError } = useGetIncidentNotes(date);

  const onSubmitForm = handleSubmit((data) =>
    isEdit ? putUpdateVehicle(data) : postAddVehicle(data),
  );

  const onClose = () => {
    closeModal();
    resetForm();
    resetStatus();
    resetUpdateStatus();
  };

  const filteredStations = useMemo(() => {
    if (!isOpPointResponseSuccess || !operationalPointResponse?.railStations)
      return [];

    const query = searchQuery.toLowerCase().trim();
    if (!query) return [];

    return operationalPointResponse.railStations.filter((station) =>
      station.name.toLowerCase().includes(query),
    );
  }, [searchQuery, operationalPointResponse, isOpPointResponseSuccess]);

  const onSearchSelectionChange = (selectedStation: string) => {
    const selected = filteredStations.find((s) => s.name === selectedStation);
    if (!selected) return;

    const nsrCode =
      selected.externalIds.find((e) => e.sourceSystem === "NSR")?.id ?? null;
    const jbvId =
      selected.externalIds.find((e) => e.sourceSystem === "TRAINPLAN")?.id ??
      null;

    setValue("baseStopReference", {
      nsrCode,
      jbvId,
      uicCode: null,
      hastusCode: null,
      nsrStopPlace: null,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <ActionModal
        title={
          isEdit
            ? "Rediger reservebuss"
            : `Legg til ${isReserve ? "reserve" : "beredskap"}`
        }
        actionTitle={
          isEdit
            ? "Lagre endringer"
            : `Legg til ${isReserve ? "reserve" : "beredskap"}`
        }
        onClose={onClose}
        onSubmit={onSubmitForm}
        successMessage={isEdit ? "Endringer lagret" : "Kjøretøy lagret"}
        failureMessage={
          isEdit ? "Lagring av endringer feilet" : "Lagring av kjøretøy feilet"
        }
        size="lg"
        loading={mutationStatus === "pending"}
        isSuccess={mutationStatus === "success"}
        isError={mutationStatus === "error"}
      >
        {(isOpPointResponseError || isNotesError) && (
          <DropsStaticAlert
            variant="error"
            title="Feil ved henting av nødvendig data"
            style={{ width: "100%" }}
          >
            Det oppstod en feil ved henting av nødvendig data. Det er ikke mulig
            å sette opp busser akkurat nå. Vennligst prøv igjen senere.
          </DropsStaticAlert>
        )}

        <VStack gap="1.2rem" alignItems="start" width="100%">
          {!isEdit && (
            <>
              <Text fontWeight="700">Velg type buss:</Text>
              <Controller
                name="serviceType"
                control={formMethods.control}
                render={({ field }) => (
                  <RadioGroupStyled
                    defaultValue={field.value}
                    value={field.value}
                    onValueChange={(details) => {
                      field.onChange(details.value);
                      if (details.value !== "RESERVE")
                        setValue("iSirkId", null);
                    }}
                  >
                    <Radio value="RESERVE">Reserve</Radio>
                    <Radio value="BACKUP">Beredskapsbuss</Radio>
                  </RadioGroupStyled>
                )}
              />
            </>
          )}
          <DropsStaticAlert variant="info" style={{ width: "100%" }}>
            {isReserve
              ? "Denne bussen vil bli synlig for kundeveiledere."
              : "Denne bussen vil kun være synlig for trafikkplanlegger."}
          </DropsStaticAlert>

          <Field
            invalid={!!errors.vehicleType}
            errorText="Kjøretøytype må velges"
            width="100%"
            aria-invalid={!!errors.vehicleType}
            aria-describedby={
              errors.vehicleType ? "vehicleType-error" : undefined
            }
          >
            <Select
              label="Type kjøretøy"
              placeholder="Velg type kjøretøy"
              {...register("vehicleType")}
            >
              <option value={vehicleTypeSchema.Values.BUS}>Buss</option>
              <option value={vehicleTypeSchema.Values.MINIBUS}>Minibuss</option>
            </Select>
          </Field>
          <Text fontWeight="700">Tilgjengelig fra og til</Text>
          <TimedDuration
            fromTimeField="shiftStartTime"
            toTimeField="shiftEndTime"
          />
          <Field
            invalid={!!errors.operatorName}
            errorText="Selskap må fylles ut"
            width="100%"
            aria-invalid={!!errors.operatorName}
            aria-describedby={
              errors.operatorName ? "operatorName-error" : undefined
            }
          >
            <Input label="Selskap" {...register("operatorName")} />
          </Field>
          <Field
            invalid={!!errors.driverPhoneNumber}
            errorText="Telefonnummer må fylles ut"
            width="100%"
            aria-invalid={!!errors.driverPhoneNumber}
            aria-describedby={
              errors.driverPhoneNumber ? "driverPhoneNumber-error" : undefined
            }
          >
            <Input label="Telefonnummer" {...register("driverPhoneNumber")} />
          </Field>
          <Field
            invalid={!!errors.baseStopReference}
            errorText="Stasjon må velges"
            width="100%"
            aria-invalid={!!errors.baseStopReference}
            aria-describedby={
              errors.baseStopReference ? "baseStopReference-error" : undefined
            }
          >
            <Combobox
              label="Tilknyttet stasjon"
              onInputChange={setSearchQuery}
              inputValue={searchQuery}
              onSelectionChange={(key) =>
                onSearchSelectionChange(key as string)
              }
            >
              {filteredStations.map((station) => (
                <Item key={station.name} textValue={station.name}>
                  {station.name}
                </Item>
              ))}
            </Combobox>
          </Field>
          <Field
            invalid={!!errors.stopName}
            errorText="Stoppnavn må oppgis"
            width="100%"
            aria-invalid={!!errors.stopName}
            aria-describedby={errors.stopName ? "stopName-error" : undefined}
          >
            <Input label="Plassering" {...register("stopName")} />
          </Field>
          {isReserve && (
            <>
              <Text fontWeight="700">Knytt til I-Sirk</Text>
              <RadioGroupStyled
                defaultValue={isIsirkSelected ? "isirk" : "referansenummer"}
                onValueChange={(details) => {
                  setIsIsirkSelected(details.value === "isirk");
                  if (details.value !== "isirk") setValue("iSirkId", null);
                }}
                value={isIsirkSelected ? "isirk" : "referansenummer"}
              >
                <Radio value="isirk">Knytt til I-sirk</Radio>
                <Radio value="referansenummer">Bruk kun referansenummer</Radio>
              </RadioGroupStyled>
            </>
          )}
          {isReserve && isIsirkSelected && (
            <Field width="100%">
              <Select
                label="Velg I-isirk"
                placeholder={
                  notes?.incidentNotes.length === 0
                    ? "Ingen i-isirk tilgjengelig denne datoen"
                    : "Velg i-isirk"
                }
                {...register("iSirkId")}
                defaultValue={undefined}
              >
                {notes?.incidentNotes.map((note) => (
                  <option key={note.identifier} value={note.isirk}>
                    {note.isirk}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Input label="Referansenummer" {...register("referenceNumber")} />
        </VStack>
      </ActionModal>
    </FormProvider>
  );
};

export default AddVehicle;
